import { CloseRounded, InfoOutlined, ScheduleRounded } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Drawer,
  FormControlLabel,
  IconButton,
  Slider,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import CauseSelector from "@/components/charity/CauseSelector";
import { useBuyCredits } from "@/hooks/useCredits";
import type { CauseSelection } from "@/interfaces/charity";
import type { CreditSummary } from "@/interfaces/credits";
import type { PaymentMethod } from "@/interfaces/paymentMethod";
import { buyAmountError, buyFloorUsd, initialBuyUsd } from "./buyCredits-helper";

// Amount is chosen in USD (matching the prototype's $ chips), credits are derived from the rate.
const USD_CHIPS = [100, 250, 500, 1000, 2500, 5000];
const MIN_USD = 50;
const MAX_USD = 10000;

const DEFAULT_NO_CARD_MESSAGE = "Select a funding card in the wallet header.";

const fmtInt = (n: number) => n.toLocaleString("en-US");
const fmtUsd = (n: number) =>
  `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Buy credits — a right-anchored slide-in drawer (matches the client-experience prototype). The
 * card is the group's funding card (chosen in the wallet header, shown here in Order Summary). No
 * Stripe Elements; buying is our own POST → poll.
 *
 * Cause beneficiaries are chosen HERE, with the same `CauseSelector` the campaign builder's Step 4
 * uses — one component, so a client sees the same causes, the same even split and the same detail
 * panel wherever they are asked. No roll-over switch: these credits have nowhere to roll over TO,
 * their expiry is the end of the line.
 *
 * Choosing is optional and the footer's confirmation says so either way: an empty selection means
 * the quarter's default beneficiary receives whatever is unused, which is the promise that no
 * credit ever expires into nothing.
 */
export default function BuyCreditsModal({
  open,
  groupId,
  groupName,
  summary,
  card,
  onClose,
  initialUsd,
  minUsd,
  onPurchased,
  noCardMessage = DEFAULT_NO_CARD_MESSAGE,
}: {
  open: boolean;
  groupId: number;
  groupName: string;
  summary: CreditSummary | undefined;
  card: PaymentMethod | null;
  onClose: () => void;
  /** Pre-fills the amount (rounded up, never below the floor). Omitted: the modal starts at $500. */
  initialUsd?: number;
  /** Raises the $50 floor; amounts below it are refused. Omitted: no floor check. */
  minUsd?: number;
  /** Called when a purchase did not fail, before the modal shows its result. */
  onPurchased?: (purchaseId: number, outcome: "success" | "pending") => void;
  /** Shown when the wallet has no funding card. Omitted: the Wallet header's own wording. */
  noCardMessage?: string;
}) {
  const buy = useBuyCredits(groupId);

  const [usd, setUsd] = useState(() => initialBuyUsd(initialUsd, minUsd));
  const [causes, setCauses] = useState<CauseSelection[]>([]);
  const [acked, setAcked] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<"success" | "pending" | null>(null);

  useEffect(() => {
    if (open && initialUsd !== undefined) setUsd(initialBuyUsd(initialUsd, minUsd));
  }, [open, initialUsd, minUsd]);

  const rate = summary?.creditsPerUsd ?? null; // credits per $1
  const credits = useMemo(() => (rate ? Math.round(usd * rate) : 0), [usd, rate]);
  const sliderMin = minUsd === undefined ? MIN_USD : buyFloorUsd(minUsd);
  const dollarsPerCredit = rate ? 1 / rate : null;

  const expiryMonths = summary?.expiryMonths ?? 12;
  // The expiry date these credits will get: purchased today + the expiry period. Computed here so
  // the copy stays accurate to system_parameters.
  const expiryDateLabel = useMemo(() => {
    const d = new Date();
    d.setMonth(d.getMonth() + expiryMonths);
    return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  }, [expiryMonths]);

  function handleClose() {
    if (buy.isPending) return; // don't close mid-charge
    setError(null);
    setDone(null);
    setAcked(false);
    setCauses([]);
    buy.reset();
    onClose();
  }

  async function handleBuy() {
    setError(null);
    if (!Number.isFinite(usd) || usd <= 0) return setError("Enter an amount.");
    if (!card) return setError(noCardMessage);
    if (rate == null) return setError("Credit pricing isn't configured. Contact support.");
    const amountError = buyAmountError(usd, minUsd);
    if (amountError) return setError(amountError);

    try {
      const { outcome, purchaseId } = await buy.mutateAsync({
        credits,
        paymentMethodId: card.id,
        // Ids only, in pick order — the even split is derived server-side.
        causes: causes.map((c) => c.causeProductId),
      });
      if (outcome === "failed") return setError("The payment was declined. Try another card.");
      onPurchased?.(purchaseId, outcome);
      setDone(outcome);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't complete the purchase.");
    }
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      slotProps={{
        paper: { sx: { width: { xs: "100%", sm: 460 }, display: "flex", flexDirection: "column" } },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 2.25,
          borderBottom: "1px solid var(--color-smoke)",
          flexShrink: 0,
        }}
      >
        <Typography sx={{ fontFamily: "var(--font-crimson)", fontSize: 22 }}>
          Buy credits · {groupName}
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseRounded />
        </IconButton>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          p: 3,
          display: "flex",
          flexDirection: "column",
          gap: 2.5,
        }}
      >
        {done ? (
          <Box sx={{ textAlign: "center", py: 5 }}>
            <Typography sx={{ fontSize: 20, fontWeight: 600, mb: 1 }}>
              {done === "success" ? "Credits added 🎉" : "Payment processing…"}
            </Typography>
            <Typography sx={{ fontSize: 14, color: "var(--color-graphite)", mb: 3 }}>
              {done === "success"
                ? `${fmtInt(credits)} credits are now in ${groupName}'s wallet.`
                : "Your card was charged; the credits will appear once payment settles."}
            </Typography>
            <Button
              variant="contained"
              onClick={handleClose}
              sx={{ bgcolor: "var(--color-rodeo-black)" }}
            >
              Done
            </Button>
          </Box>
        ) : (
          <>
            {/* Select amount */}
            <Box
              sx={{ border: "1px solid var(--color-card-border)", borderRadius: "12px", p: 2.25 }}
            >
              <Typography sx={{ fontFamily: "var(--font-crimson)", fontSize: 18, mb: 1 }}>
                Select amount
              </Typography>

              {/* Hero — the amount IS the input: big crimson, editable, dashed underline. */}
              <Box sx={{ textAlign: "center", pt: 0.5 }}>
                <Box
                  sx={{ display: "inline-flex", alignItems: "baseline", justifyContent: "center" }}
                >
                  <Box
                    component="span"
                    sx={{
                      fontFamily: "var(--font-crimson)",
                      fontSize: 28,
                      color: "var(--color-graphite)",
                      mr: "2px",
                      lineHeight: 1,
                    }}
                  >
                    $
                  </Box>
                  <Box
                    component="input"
                    type="number"
                    value={usd}
                    onChange={(e) =>
                      setUsd(Math.max(0, Math.floor(Number((e.target as HTMLInputElement).value))))
                    }
                    sx={{
                      fontFamily: "var(--font-crimson)",
                      fontSize: 52,
                      fontWeight: 400,
                      color: "var(--color-rodeo-black)",
                      border: "none",
                      borderBottom: "2px dashed var(--color-smoke)",
                      background: "transparent",
                      outline: "none",
                      width: 160,
                      textAlign: "center",
                      p: 0,
                      lineHeight: 1.1,
                      fontVariantNumeric: "tabular-nums",
                      caretColor: "var(--color-dark-dust)",
                      transition: "border-color .15s",
                      MozAppearance: "textfield",
                      "&:hover": { borderBottomColor: "var(--color-graphite)" },
                      "&:focus": {
                        borderBottomStyle: "solid",
                        borderBottomColor: "var(--color-dark-dust)",
                      },
                      "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
                        WebkitAppearance: "none",
                        margin: 0,
                      },
                    }}
                  />
                </Box>
                <Typography sx={{ fontSize: 13, color: "var(--color-graphite)", mt: 0.75 }}>
                  <Box component="span" sx={{ color: "var(--color-rodeo-black)", fontWeight: 700 }}>
                    {fmtInt(credits)}
                  </Box>{" "}
                  credits
                </Typography>
                <Typography sx={{ fontSize: 12, color: "var(--color-graphite)", opacity: 0.85 }}>
                  Type any amount or pick below
                </Typography>
              </Box>

              {/* Oval quick-pick pills — selected = light-dust fill + dark-dust border (not black). */}
              <Box
                sx={{ display: "flex", flexWrap: "wrap", gap: 1, justifyContent: "center", mt: 2 }}
              >
                {USD_CHIPS.map((v) => {
                  const on = usd === v;
                  return (
                    <Box
                      key={v}
                      component="button"
                      type="button"
                      onClick={() => setUsd(v)}
                      sx={{
                        px: 2,
                        py: "7px",
                        borderRadius: "999px",
                        border: `1px solid ${on ? "var(--color-dark-dust)" : "var(--color-smoke)"}`,
                        bgcolor: on ? "var(--color-light-dust)" : "var(--color-white)",
                        color: "var(--color-rodeo-black)",
                        fontFamily: "var(--font-lato)",
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: "pointer",
                        fontVariantNumeric: "tabular-nums",
                        transition: "all .15s",
                        "&:hover": on
                          ? {}
                          : {
                              borderColor: "var(--color-rodeo-dust)",
                              bgcolor: "var(--color-clear-dust)",
                            },
                      }}
                    >
                      {fmtUsd(v).replace(".00", "")}
                    </Box>
                  );
                })}
              </Box>

              <Slider
                value={Math.min(Math.max(usd, sliderMin), MAX_USD)}
                onChange={(_e, v) => setUsd(v as number)}
                min={sliderMin}
                max={MAX_USD}
                step={10}
                sx={{ mt: 1.5, color: "var(--color-dark-dust)" }}
              />
              <Box sx={{ display: "flex", justifyContent: "space-between", px: 0.5 }}>
                <Typography sx={{ fontSize: 11.5, color: "var(--color-graphite)" }}>
                  {fmtUsd(sliderMin).replace(".00", "")}
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: "var(--color-graphite)" }}>
                  {fmtUsd(MAX_USD).replace(".00", "")}
                </Typography>
              </Box>
            </Box>

            {/* Cause beneficiary — where whatever is unused goes when these credits expire. The
                same selector as the campaign builder's Step 4, with no roll-over switch: a
                purchased lot's expiry is the end of the line, there is nothing to roll over to. */}
            <Box
              sx={{ border: "1px solid var(--color-card-border)", borderRadius: "12px", p: 2.25 }}
            >
              <CauseSelector value={causes} onChange={setCauses} />
              {causes.length === 0 && (
                <Typography
                  sx={{ fontSize: 12.5, color: "var(--color-graphite)", mt: 1.5, lineHeight: 1.5 }}
                >
                  Optional. If you don't choose, anything unused goes to this quarter's featured
                  cause — credits are never lost.
                </Typography>
              )}
            </Box>

            {/* Order summary */}
            <Box sx={{ border: "1px solid var(--color-card-border)", borderRadius: "12px", p: 2 }}>
              <Typography sx={{ fontFamily: "var(--font-crimson)", fontSize: 18, mb: 1.5 }}>
                Order Summary
              </Typography>

              <SectionLabel>Payment method</SectionLabel>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  border: "1px solid var(--color-card-border)",
                  borderRadius: "8px",
                  px: 1.5,
                  py: 1.25,
                  mb: 2,
                }}
              >
                {card ? (
                  <Box>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>
                      {(card.brand ?? "Card").toUpperCase()} •••• {card.last4 ?? "••••"}
                    </Typography>
                    {card.expMonth && card.expYear && (
                      <Typography sx={{ fontSize: 12, color: "var(--color-graphite)" }}>
                        Expires {String(card.expMonth).padStart(2, "0")}/{card.expYear}
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Typography sx={{ fontSize: 13, color: "var(--color-good-wine)" }}>
                    No funding card selected
                  </Typography>
                )}
                <Button
                  size="small"
                  onClick={handleClose}
                  sx={{ color: "var(--color-dark-dust)", minWidth: 0 }}
                >
                  Change
                </Button>
              </Box>

              <SectionLabel>Cost summary</SectionLabel>
              <Row label="Credits" value={`${fmtInt(credits)} credits`} />
              <Row
                label="Rate"
                value={dollarsPerCredit != null ? `${fmtUsd(dollarsPerCredit)} / credit` : "—"}
              />
              <Box sx={{ borderTop: "1px dashed var(--color-card-border)", my: 1 }} />
              <Row label="Subtotal" value={fmtUsd(usd)} bold />
            </Box>
          </>
        )}
      </Box>

      {/* Footer */}
      {!done && (
        <Box sx={{ px: 3, py: 2, borderTop: "1px solid var(--color-smoke)", flexShrink: 0 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.75,
              py: 0.75,
              borderRadius: "8px",
              bgcolor: "var(--color-wax)",
              color: "var(--color-graphite)",
              fontSize: 12.5,
            }}
          >
            <ScheduleRounded sx={{ fontSize: 15 }} /> Credits expire in {expiryMonths} months
            <Tooltip
              arrow
              title={
                causes.length > 0
                  ? `Any balance unused by ${expiryDateLabel} is automatically donated to the cause(s) you selected, split evenly — so nothing goes to waste.`
                  : `Any balance unused by ${expiryDateLabel} is automatically donated to this quarter's featured cause — so nothing goes to waste.`
              }
            >
              <InfoOutlined sx={{ fontSize: 15, cursor: "help", opacity: 0.7 }} />
            </Tooltip>
          </Box>

          {/* Purchase confirmation — required before buying. */}
          <FormControlLabel
            sx={{ alignItems: "flex-start", mx: 0, mt: 1.25 }}
            control={
              <Checkbox
                checked={acked}
                onChange={(e) => setAcked(e.target.checked)}
                size="small"
                sx={{
                  pt: 0,
                  mr: 0.5,
                  color: "var(--color-dark-dust)",
                  "&.Mui-checked": { color: "var(--color-dark-dust)" },
                }}
              />
            }
            label={
              <Typography sx={{ fontSize: 12, color: "var(--color-graphite)", lineHeight: 1.4 }}>
                I understand that any unused gift credits I purchase today will expire in{" "}
                {expiryMonths} months and be sent to{" "}
                {causes.length > 0
                  ? "the beneficiary causes I have selected"
                  : "this quarter's featured beneficiary cause"}
                .
              </Typography>
            }
          />

          <Box
            sx={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              mt: 1,
              mb: 1.25,
            }}
          >
            <Typography
              sx={{ fontSize: 13, letterSpacing: "0.04em", color: "var(--color-dark-dust)" }}
            >
              TOTAL (USD)
            </Typography>
            <Typography sx={{ fontFamily: "var(--font-crimson)", fontSize: 26 }}>
              {fmtUsd(usd)}
            </Typography>
          </Box>
          {/* In the fixed footer, right above the button, so a refusal is seen without scrolling. */}
          {error && (
            <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 1.25 }}>
              {error}
            </Alert>
          )}
          <Button
            variant="contained"
            fullWidth
            onClick={handleBuy}
            disabled={buy.isPending || !acked}
            sx={{ bgcolor: "var(--color-rodeo-black)", py: 1.1 }}
          >
            {buy.isPending ? (
              <CircularProgress aria-label="Loading" size={20} sx={{ color: "white" }} />
            ) : (
              "Buy credits"
            )}
          </Button>
        </Box>
      )}
    </Drawer>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      sx={{
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: "0.05em",
        textTransform: "uppercase",
        color: "var(--color-dark-dust)",
        mb: 0.75,
      }}
    >
      {children}
    </Typography>
  );
}

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.4 }}>
      <Typography sx={{ fontSize: 13.5, color: "var(--color-graphite)" }}>{label}</Typography>
      <Typography sx={{ fontSize: 13.5, fontWeight: bold ? 700 : 500 }}>{value}</Typography>
    </Box>
  );
}
